import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABEL } from "@/lib/constants";
import {
  LayoutDashboard,
  Users2,
  ClipboardCheck,
  Map,
  ShieldCheck,
  ScrollText,
  Settings,
  Newspaper,
  Trash2,
  LogOut,
  Vote,
  Menu,
  X,
  ChevronRight,
  Upload,
  FileDown,
  GitCompare,
  Lock,
  Sparkles,
  History,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "./UserAvatar";
import { ProfileDialog } from "./ProfileDialog";
import { NotificationBell } from "./NotificationBell";
import OfflineStatusBadge from "./OfflineStatusBadge";

function navGroupsFor(role) {
  const groups = [
    {
      key: "coklit",
      label: "Coklit Harian",
      roles: ["admin", "panitia", "ppdp", "rt", "rw"],
      items: [
        { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "panitia", "ppdp", "rt", "rw"] },
        { to: "/app/coklit", label: "Coklit", icon: ClipboardCheck, roles: ["admin", "panitia", "ppdp"] },
        { to: "/app/voters", label: "Data Pemilih", icon: Users2, roles: ["admin", "panitia", "ppdp", "rt", "rw"] },
        { to: "/app/perubahan-saya", label: "Perubahan Saya", icon: History, roles: ["ppdp", "admin", "panitia"] },
      ],
    },
    {
      key: "manajemen",
      label: "Manajemen",
      roles: ["admin", "panitia", "rt", "rw"],
      items: [
        { to: "/app/dps-delta", label: "Delta & Log Perubahan", icon: GitCompare, roles: ["admin", "panitia", "rt", "rw"] },
        { to: "/app/lock-progress", label: "Status Lock TPS", icon: Lock, roles: ["admin", "panitia"] },
        { to: "/app/wilayah", label: "Wilayah", icon: Map, roles: ["admin", "panitia"] },
        { to: "/app/ppdp-mapping-simulasi", label: "Pemetaan TPS", icon: Sparkles, roles: ["admin", "panitia"] },
        { to: "/app/users", label: "Pengguna", icon: Users2, roles: ["admin"] },
        { to: "/app/rw-officers", label: "Pengurus RW", icon: Users2, roles: ["admin"] },
        { to: "/app/news", label: "Berita", icon: Newspaper, roles: ["admin", "panitia"] },
      ],
    },
    {
      key: "admin",
      label: "Admin Tools",
      roles: ["admin", "panitia"],
      items: [
        { to: "/app/import", label: "Impor DPS", icon: Upload, roles: ["admin", "panitia"] },
        { to: "/app/reports", label: "Laporan & BA", icon: FileDown, roles: ["admin", "panitia"] },
        { to: "/app/reset-voters", label: "Reset Data Pemilih", icon: Trash2, roles: ["admin"] },
        { to: "/app/recycle-bin", label: "Recycle Bin", icon: Trash2, roles: ["admin"] },
        { to: "/app/audit", label: "Audit Log", icon: ScrollText, roles: ["admin", "panitia"] },
        { to: "/app/settings", label: "Pengaturan", icon: Settings, roles: ["admin"] },
      ],
    },
  ];
  return groups
    .map((g) => ({ ...g, items: g.items.filter((i) => i.roles.includes(role)) }))
    .filter((g) => g.items.length > 0);
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  if (!user) return null;
  const groups = navGroupsFor(user.role);
  const isPPDP = user.role === "ppdp";

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  // Mobile-first bottom navigation for PPDP
  const mobileBottomNav = isPPDP
    ? [
        { to: "/app/dashboard", label: "Progress", icon: LayoutDashboard },
        { to: "/app/coklit", label: "Coklit", icon: ClipboardCheck },
        { to: "/app/voters", label: "Pemilih", icon: Users2 },
      ]
    : null;

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-64 lg:w-72 shrink-0 flex-col border-r border-slate-200 bg-white sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-slate-900 text-white grid place-items-center">
              <Vote className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-lg font-bold tracking-tight">SIPILKADES</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Desa Mekarsari</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-4 space-y-4">
          {groups.map((g) => (
            <div key={g.key} className="space-y-1">
              <div className="px-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
                {g.label}
              </div>
              {g.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.to.split("/").pop()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold transition-colors ${
                      isActive
                        ? "bg-slate-900 text-white"
                        : "text-slate-700 hover:bg-slate-100"
                    }`
                  }
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center justify-between mb-2 px-1">
            <button
              type="button"
              onClick={() => setProfileOpen(true)}
              className="flex-1 flex items-center gap-3 px-2 py-2 rounded-md hover:bg-slate-100 transition-colors text-left"
              data-testid="open-profile-button"
            >
              <UserAvatar user={user} size={36} />
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">{user.name}</div>
                <div className="text-xs text-slate-500">{ROLE_LABEL[user.role] || user.role}</div>
              </div>
            </button>
            <OfflineStatusBadge />
            <NotificationBell />
          </div>
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" /> Keluar
          </Button>
        </div>
      </aside>

      {/* Mobile top header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-slate-900 text-white grid place-items-center">
              <Vote className="h-4 w-4" />
            </div>
            <div className="font-display font-bold tracking-tight">SIPILKADES</div>
          </div>
          <button
            onClick={() => setSidebarOpen((v) => !v)}
            className="p-2 -mr-2"
            data-testid="mobile-menu-toggle"
            aria-label="Buka menu"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile top-right notification bell (fixed) */}
      <div className="md:hidden fixed top-2 right-12 z-40 flex items-center gap-1">
        <OfflineStatusBadge />
        <NotificationBell />
      </div>

      {/* Mobile drawer */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-slate-900/50"
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="absolute right-0 top-0 bottom-0 w-72 bg-white shadow-2xl flex flex-col">
            <div className="px-5 py-5 border-b border-slate-200 flex items-center justify-between">
              <button
                type="button"
                onClick={() => { setProfileOpen(true); setSidebarOpen(false); }}
                className="flex items-center gap-3 text-left min-w-0"
                data-testid="open-profile-button-mobile"
              >
                <UserAvatar user={user} size={36} />
                <div className="min-w-0">
                  <div className="font-semibold truncate">{user.name}</div>
                  <div className="text-xs text-slate-500">{ROLE_LABEL[user.role] || user.role}</div>
                </div>
              </button>
              <button onClick={() => setSidebarOpen(false)} aria-label="Tutup menu">
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-3 space-y-4">
              {groups.map((g) => (
                <div key={g.key} className="space-y-1">
                  <div className="px-3 pb-1 text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
                    {g.label}
                  </div>
                  {g.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center justify-between rounded-md px-3 py-3 text-sm font-semibold transition-colors ${
                          isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                        }`
                      }
                    >
                      <span className="flex items-center gap-3">
                        <item.icon className="h-4 w-4" />
                        {item.label}
                      </span>
                      <ChevronRight className="h-4 w-4 opacity-50" />
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>
            <div className="p-4 border-t border-slate-200">
              <Button variant="outline" className="w-full" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" /> Keluar
              </Button>
            </div>
          </aside>
        </div>
      )}

      {/* Main content */}
      <main className={`flex-1 min-w-0 ${isPPDP ? "pb-20" : "pb-6"} pt-14 md:pt-0`}>
        <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>

      {/* PPDP mobile bottom nav */}
      {mobileBottomNav && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-30">
          <div className="grid grid-cols-3">
            {mobileBottomNav.map((item) => {
              const active = location.pathname.startsWith(item.to);
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`bottom-nav-${item.label.toLowerCase()}`}
                  className={`flex flex-col items-center justify-center py-3 text-xs font-semibold ${
                    active ? "text-slate-900" : "text-slate-500"
                  }`}
                >
                  <item.icon className={`h-5 w-5 mb-1 ${active ? "text-slate-900" : "text-slate-400"}`} />
                  {item.label}
                </NavLink>
              );
            })}
          </div>
        </nav>
      )}

      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
    </div>
  );
}
